// @flow

import React, { useMemo } from 'react';
import { useEffect } from 'react';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import HccSourceCard from './HccSourceCard';
import ActionModal from './ActionModal';
import InternalNoteModal from './InternalNoteModal';
import HipaaLogModal from './HipaaLogModal';
import PdfModal from './PdfModal';
import CdaModal from './CdaModal';
import { useGetPatientHccsQuery } from '../../api/api';
import { format } from 'date-fns';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export const WORKFLOW_ACTION_SLUG_SUFFIXES = {
  QUERY_PROVIDER: 'query_provider',
  DENY: 'deny',
  INTERNAL_NOTE: 'internal_note',
  CONFIRM: 'confirm',
  BACK_TO_CODER: 'back_to_coder'
};
const LAST_SENT_TO_NLP_TOOL_TIP_TEXT =
  'The last date any chart for this patient ' +
  'finished NLP processing. This updates each time an individual chart completes NLP. ' +
  'If blank, this patient has not yet been sent to NLP.';
const LAST_ACTIVITY_DATE_TOOL_TIP =
  'The last date any action was taken on this patient, including chart retrieval, ' +
  'demographic updates, or NLP processing. Use this date to confirm that work has been ' +
  'conducted on this patient recently, regardless of whether clinical data changed.';

export const cleanHccLabel = (label: string): string => {
  if (!label) return '';

  // If label starts with HCC, remove everything inside parentheses
  if (label.startsWith('HCC')) {
    return 'HCC';
  }

  return label;
};

export const getButtonClass = (
  slug: string,
  classes: { [key: string]: string }
) => {
  switch (true) {
    case slug.endsWith(WORKFLOW_ACTION_SLUG_SUFFIXES.QUERY_PROVIDER):
      return classes.query_provider;
    case slug.endsWith(WORKFLOW_ACTION_SLUG_SUFFIXES.DENY):
      return classes.deny;
    case slug.endsWith(WORKFLOW_ACTION_SLUG_SUFFIXES.INTERNAL_NOTE):
      return classes.addInternalNote;
    case slug.endsWith(WORKFLOW_ACTION_SLUG_SUFFIXES.CONFIRM):
      return classes.confirm;
    case slug.endsWith(WORKFLOW_ACTION_SLUG_SUFFIXES.BACK_TO_CODER):
      return classes.back_to_coder;
  }

  return '';
};

type Props = {
  patientId?: string;
};

const RELOAD_PATIENT_HCCS_INTERVAL = 1000 * 60; // 1 minute

const PatientHCCs = (props: Props): JSX.Element => {
  const { patientId = '' } = props;

  const { data, isLoading: patientHccLoading } = useGetPatientHccsQuery(
    patientId,
    {
      pollingInterval: RELOAD_PATIENT_HCCS_INTERVAL,
      skipPollingIfUnfocused: true,
      selectFromResult: ({ data, error, isLoading }) => ({
        data,
        isLoading
      })
    }
  );
  const patientHccs = useMemo(() => data?.codes, [data]);
  const pickOpenEncounterToAcceptICD = useMemo(
    () => data?.pickOpenEncounterToAcceptICD,
    [data]
  );
  const encounters = useMemo(() => data?.encounters, [data]);
  const hccStats = useMemo(
    () => ({
      currentCodesCount: data?.currentCodesCount,
      currentSourcesCount: data?.currentSourcesCount,
      totalCodesCount: data?.totalCodesCount,
      totalSourcesCount: data?.totalSourcesCount,
      lastRefreshed: data?.compendiumLastRefreshed,
      lastSentToNlp: data?.lastSentToNlp,
      lastActivity: data?.lastActivity
    }),
    [data]
  );

  useEffect(() => {
    console.log('[PatientHccs] useEffect.......');
  }, []);

  const Main = () => (
    <>
      {patientHccLoading ? (
        <div className="flex items-start justify-center w-full mt-[80px]">
          <CircularProgress />
        </div>
      ) : patientHccs ? (
        <>
          {/* <div style={{ marginBottom: 40 }}>
            <Button onClick={exportCodesToCSV} disabled={loadingCSV} variant="outlined">
              {loadingCSV ? 'Exporting...' : 'Export to CSV'}
            </Button>
          </div> */}
          <div className="mt-[80px]">
            <div>
              {/* <HCCFilter
            setUrlQueryParams={setUrlQueryParams}
            loading={loadingPatientData}
          /> */}
            </div>
            <div className="flex flex-row justify-between items-start">
              <div>
                {data?.patient && (
                  <>
                    {data?.patient.firstName && data?.patient.lastName && (
                      <Typography variant="h1" className="!mt-3 !font-semibold">
                        {data?.patient.firstName} {data?.patient.lastName}
                      </Typography>
                    )}
                    {/* <div>
                  <Typography>
                    Date of Birth: <strong>{patientData.dob}</strong>
                  </Typography>
                  <Typography>
                    Patient ID: <strong>{patientData.id}</strong>
                  </Typography>
                </div> */}
                  </>
                )}
              </div>
              <div className="flex flex-col gap-2 !mt-3">
                <div className="text-right">
                  <div className="my-2 flex flex-col gap-2">
                    <div className="text-right">
                      <div className="flex flex-row items-center">
                        <Tooltip
                          title={LAST_SENT_TO_NLP_TOOL_TIP_TEXT}
                          placement="left"
                        >
                          <InfoOutlinedIcon
                            fontSize="small"
                            sx={{
                              color: 'gray',
                              marginRight: '5px'
                            }}
                          />
                        </Tooltip>
                        {hccStats.lastSentToNlp ? (
                          <time
                            dateTime={hccStats.lastSentToNlp}
                            style={{ lineHeight: '1.05rem' }}
                          >
                            {format(
                              new Date(hccStats.lastSentToNlp),
                              'MMM dd yyyy p (z)'
                            )}
                          </time>
                        ) : (
                          <span className="w-full">&mdash;</span>
                        )}
                      </div>
                      <span className="text-[0.8rem] w-full">
                        Last Sent to NLP
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="flex flex-row items-center">
                        <Tooltip
                          title={LAST_ACTIVITY_DATE_TOOL_TIP}
                          placement="left"
                        >
                          <InfoOutlinedIcon
                            fontSize="small"
                            sx={{
                              color: 'gray',
                              marginRight: '5px'
                            }}
                          />
                        </Tooltip>
                        {hccStats.lastActivity ? (
                          <time
                            dateTime={hccStats.lastActivity}
                            style={{ lineHeight: '1.05rem' }}
                          >
                            {format(
                              new Date(hccStats.lastActivity),
                              'MMM dd yyyy p (z)'
                            )}
                          </time>
                        ) : (
                          <span className="w-full">&mdash;</span>
                        )}
                      </div>
                      <span className="text-[0.8rem] w-full">
                        Last Patient Activity
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              {Object.keys(patientHccs).map((section) => (
                <div key={section} className="mb-5">
                  <div className="flex justify-between">
                    {/* eslint-disable-next-line max-len */}
                    <Typography className="uppercase !font-bold !text-[2rem] text-mineShaft/60 font-dmSans leading-13 tracking-[-0.03em]">
                      {section}
                    </Typography>
                  </div>
                  <div className="mt-3">
                    {patientHccs[section] ? (
                      Object.keys(patientHccs[section]).length ? (
                        <>
                          {Object.keys(patientHccs[section]).map((hcc) => {
                            const hccData = patientHccs[section][hcc];
                            const hccItems = Array.isArray(hccData)
                              ? hccData
                              : hccData.hccs || [];

                            let label = hcc;
                            let hccCategories = [];
                            let hccCategoryDescriptions = [];
                            let hccCmsVersions = [];

                            if (
                              !Array.isArray(hccData) &&
                              hccData.hccCategory
                            ) {
                              hccCategories = hccData.hccCategory;
                              hccCategoryDescriptions =
                                hccData.hccCategoryDescription;
                              hccCmsVersions = hccData.cmsVersion;
                              label = cleanHccLabel(hcc);
                            }

                            return (
                              <HccSourceCard
                                key={hcc}
                                _key={`${section}-${hcc}`}
                                hccLabel={label}
                                hccCategories={hccCategories}
                                hccCategoryDescriptions={
                                  hccCategoryDescriptions
                                }
                                hccCmsVersions={hccCmsVersions}
                                patientHccs={hccItems}
                                pickOpenEncounterToAcceptICD={
                                  pickOpenEncounterToAcceptICD
                                }
                                encounters={encounters}
                              />
                            );
                          })}
                        </>
                      ) : (
                        <span style={{ fontSize: '1rem' }}>
                          No results found
                        </span>
                      )
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <span style={{ fontSize: 14 }}>No results found</span>
      )}
    </>
  );

  return (
    <>
      <ActionModal />
      <InternalNoteModal />
      <HipaaLogModal />
      <PdfModal />
      <CdaModal />
      <Main />
    </>
  );
};

export default PatientHCCs;
